import { Field, Formik, type FormikHelpers } from 'formik';
import { useEffect, useRef, type ChangeEvent, type KeyboardEvent, type RefObject } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import spinnerTitle from '../others/spinnerTitle';
import { sanitizeNumericInput, toParseableNumberString } from 'utils/helpers/functions/sanitizeNumericInput';

type FormInputValue = string | number;

const isValidEmailInput = (value: string): boolean => {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    return normalized.includes('@') && normalized.includes('.');
};

interface FormInputProps {
    autoFocus?: boolean;
    autoSelect?: boolean;
    title?: string;
    showForm: boolean;
    setShowForm: (show: boolean) => void;
    /** Поверніть `false`, щоб залишити форму відкритою (наприклад, при помилці валідації/збереження) */
    callBack: (value: FormInputValue) => void | boolean | Promise<void | boolean>;
    defaultValue?: FormInputValue;
    type?: string;
    as?: 'input' | 'textarea' | string;
    rows?: number;
    compact?: boolean;
    numbersOnly?: boolean;
    decimalPlaces?: number;
    /** Не відправляти порожнє значення (після trim) */
    required?: boolean;
    requiredMessage?: string;
    /** Повідомлення при некоректному email (для type="email") */
    invalidEmailMessage?: string;
    onInput?: ((ref: RefObject<HTMLInputElement | HTMLTextAreaElement | null>) => void) | null;
}

const FormInput = ({
    autoFocus = false,
    autoSelect = false,
    title,
    showForm,
    setShowForm,
    callBack,
    defaultValue = '',
    type,
    as,
    rows = 6,
    compact = false,
    numbersOnly = false,
    decimalPlaces,
    required = false,
    requiredMessage,
    invalidEmailMessage,
    onInput = null,
}: FormInputProps) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    const isEmail = type === 'email';

    useEffect(() => {
        if (showForm) {
            inputRef.current?.focus();
        }
    }, [showForm]);

    const handleSubmit = async (
        values: { formField: FormInputValue },
        { setSubmitting, setFieldError }: FormikHelpers<{ formField: FormInputValue }>,
    ) => {
        const raw = values.formField;
        const trimmed = String(raw ?? '').trim();

        if (required && !trimmed) {
            setFieldError('formField', requiredMessage || t('empty'));
            setSubmitting(false);
            inputRef.current?.focus();
            return;
        }

        if (isEmail && trimmed && !isValidEmailInput(trimmed)) {
            setFieldError('formField', invalidEmailMessage || t('invalidEmail'));
            setSubmitting(false);
            inputRef.current?.focus();
            return;
        }

        const isNumeric = numbersOnly || decimalPlaces !== undefined;
        const valueToSave = isEmail
            ? trimmed.toLowerCase()
            : isNumeric
              ? toParseableNumberString(trimmed)
              : required
                ? trimmed
                : raw;
        const result = await callBack(valueToSave);
        if (result === false) {
            setSubmitting(false);
            return;
        }

        setShowForm(false);
        setSubmitting(false);
    };

    useEffect(() => {
        if (autoSelect) {
            inputRef.current?.select();
        }
    }, [autoSelect]);

    return (
        <div className={compact ? '' : 'mt-3'}>
            <Formik
                initialValues={{
                    formField: defaultValue,
                }}
                enableReinitialize
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, isSubmitting, setFieldValue, setFieldError, errors }) => {
                    const submitForm = () => {
                        void handleSubmit();
                    };
                    const showError = Boolean(errors.formField);

                    return (
                        <div>
                            <Form.Group className={compact ? 'mb-2' : 'mb-3'}>
                                {title ? <label className="form-label">{title}</label> : null}
                                <Field
                                    autoFocus={autoFocus}
                                    disabled={isSubmitting}
                                    name="formField"
                                    type={type || 'text'}
                                    as={as}
                                    className={`form-control${compact ? ' form-control-sm' : ''}${showError ? ' is-invalid' : ''}`}
                                    innerRef={inputRef}
                                    rows={rows}
                                    inputMode={
                                        (numbersOnly && decimalPlaces === undefined) || decimalPlaces === 0
                                            ? 'numeric'
                                            : numbersOnly || decimalPlaces !== undefined
                                              ? 'decimal'
                                              : isEmail
                                                ? 'email'
                                                : undefined
                                    }
                                    autoComplete={isEmail ? 'email' : undefined}
                                    onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                        let value = e.target.value;
                                        if (numbersOnly || decimalPlaces !== undefined) {
                                            value = sanitizeNumericInput(
                                                value,
                                                decimalPlaces ?? (numbersOnly ? 0 : undefined),
                                            );
                                        }
                                        setFieldValue('formField', value);
                                        if (errors.formField) setFieldError('formField', undefined);
                                    }}
                                    onInput={() => onInput?.(inputRef)}
                                    onKeyDown={(e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                        if (e.key !== 'Enter' || as === 'textarea') return;
                                        e.preventDefault();
                                        e.stopPropagation();
                                        submitForm();
                                    }}
                                />
                                {showError ? (
                                    <div className="invalid-feedback d-block">{errors.formField}</div>
                                ) : null}
                            </Form.Group>
                            <div className="text-end">
                                <Button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowForm(false);
                                    }}
                                    style={{ height: 24, minWidth: 54, lineHeight: 1 }}
                                    variant="secondary"
                                    size="sm"
                                    className="mb-1 py-0 px-2 fs-12"
                                    type="button"
                                >
                                    {t('buttons.cancel')}
                                </Button>
                                <Button
                                    variant="success"
                                    style={{ height: 24, minWidth: 54, lineHeight: 1 }}
                                    type="button"
                                    size="sm"
                                    className="mb-1 ms-2 py-0 px-2 fs-12"
                                    disabled={isSubmitting}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        submitForm();
                                    }}
                                >
                                    {spinnerTitle(t('buttons.save'), isSubmitting, t('buttons.saving'))}
                                </Button>
                            </div>
                        </div>
                    );
                }}
            </Formik>
        </div>
    );
};

export default FormInput;
